import { Controller } from "@hotwired/stimulus"
import L from 'leaflet'
import '../../vendor/L.TileLayer.NoGap.js'

// Static boundary/feature display map using Leaflet
// Equivalent to maplibre_basic_controller.js but using Leaflet
//
// Usage:
//   data-controller="map--leaflet-basic"
//   data-map--leaflet-basic-feature-collection-value='{"type":"FeatureCollection",...}'
//   data-map--leaflet-basic-properties-value='{"fillColor":"#627BC1",...}'
export default class extends Controller {
  static targets = ["map"]

  static values = {
    featureCollection: { type: Object, default: null },
    properties: { type: Object, default: {} },
    padding: { type: Number, default: 20 },
    iconSize: { type: Number, default: 48 },
    mapboxStyle: { type: String, default: 'mapbox/streets-v12' },
    mapboxToken: { type: String },
    center: { type: Array, default: [-97.7431, 30.2672] },
    zoom: { type: Number, default: 10 },
    minZoom: { type: Number, default: 3 },
    maxZoom: { type: Number, default: 17 },
    interactive: { type: Boolean, default: false },
    attributionControl: { type: Boolean, default: false },
    navigationControl: { type: Boolean, default: false }
  }

  connect() {
    // Skip initialization for Turbo preview
    if (document.documentElement.hasAttribute("data-turbo-preview")) {
      return
    }

    console.log("map--leaflet-basic")
    this.cleanup()
    this.initializeMap()
    this.setupResizeObserver()
  }

  disconnect() {
    this.cleanup()
  }

  initializeMap() {
    const container = this.hasMapTarget ? this.mapTarget : this.element

    this._map = L.map(container, {
      center: [this.centerValue[1], this.centerValue[0]], // Leaflet uses [lat, lng]
      zoom: this.zoomValue,
      zoomSnap: 0,
      zoomDelta: 0.25,
      maxZoom: this.maxZoomValue,
      minZoom: this.minZoomValue,
      attributionControl: this.attributionControlValue,
      zoomControl: this.navigationControlValue && this.interactiveValue,
      dragging: this.interactiveValue,
      touchZoom: this.interactiveValue,
      scrollWheelZoom: this.interactiveValue,
      doubleClickZoom: this.interactiveValue,
      boxZoom: this.interactiveValue,
      keyboard: this.interactiveValue
    })

    // Add tile layer
    this.addTileLayer()

    // When map is ready, add features
    this._map.whenReady(() => {
      this.onMapLoad()
    })
  }

  addTileLayer() {
    L.TileLayer.mergeOptions({ dumpToCanvas: false })

    this._tileLayer = L.tileLayer(
      'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
      {
        tileSize: 512,
        zoomOffset: -1,
        id: this.mapboxStyleValue,
        accessToken: this.mapboxTokenValue,
      }
    ).on('tileerror', function(error, tile) {
      console.log("[Leaflet Basic] Tile loading error:", error, tile)
    })

    this._tileLayer.addTo(this._map)
  }

  onMapLoad() {
    if (!this.featureCollectionValue) {
      console.warn("[Leaflet Basic] No feature collection provided")
      return
    }

    this.addFeatures()
    this.fitToFeatures()
  }

  // Normalize input to FeatureCollection format
  // Handles raw geometries, single Features, or FeatureCollections
  normalizeToFeatureCollection(data) {
    if (!data) return null

    // Already a FeatureCollection
    if (data.type === "FeatureCollection") {
      return data
    }

    // Single Feature
    if (data.type === "Feature") {
      return { type: "FeatureCollection", features: [data] }
    }

    // Raw geometry (Point, Polygon, MultiPolygon, etc.)
    if (["Point", "MultiPoint", "LineString", "MultiLineString", "Polygon", "MultiPolygon", "GeometryCollection"].includes(data.type)) {
      return {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          properties: {},
          geometry: data
        }]
      }
    }

    console.warn("[Leaflet Basic] Unknown GeoJSON type:", data.type)
    return null
  }

  // Add GeoJSON features to the map
  addFeatures() {
    const fc = this.normalizeToFeatureCollection(this.featureCollectionValue)
    if (!fc) {
      console.warn("[Leaflet Basic] Could not normalize feature collection")
      return
    }

    const props = this.propertiesValue

    // Store normalized FC for other methods
    this._normalizedFC = fc

    // Create GeoJSON layer with styling
    this._featureLayer = L.geoJSON(fc, {
      style: {
        fillColor: props.fillColor || "#627BC1",
        fillOpacity: props.fillOpacity ?? 0.6,
        color: props.color || "#333333",
        opacity: props.opacity ?? 1,
        weight: props.weight ?? 2
      },
      pointToLayer: (feature, latlng) => {
        if (feature.geometry.type === 'Point') {
          return L.marker(latlng, {
            icon: L.divIcon({
              className: 'custom-marker',
              html: this.createMarkerSvg(props),
              iconSize: [this.iconSizeValue, this.iconSizeValue],
              iconAnchor: [this.iconSizeValue / 2, this.iconSizeValue / 2]
            })
          })
        }
        return null
      }
    }).addTo(this._map)
  }

  // Create SVG marker matching the original Leaflet marker
  createMarkerSvg(props) {
    const fillColor = props.fillColor || "#627BC1"
    const fillOpacity = props.fillOpacity ?? 0.6
    const strokeColor = props.color || "#333333"
    const strokeOpacity = props.opacity ?? 1

    return `<svg xmlns="http://www.w3.org/2000/svg"
                fill="${fillColor}"
                fill-opacity="${fillOpacity}"
                stroke="${strokeColor}"
                stroke-opacity="${strokeOpacity}"
                stroke-width="0.5"
                viewBox="0 0 24 24">
              <path d="M18.364 17.364L12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364ZM12 15C14.2091 15 16 13.2091 16 11C16 8.79086 14.2091 7 12 7C9.79086 7 8 8.79086 8 11C8 13.2091 9.79086 15 12 15ZM12 13C10.8954 13 10 12.1046 10 11C10 9.89543 10.8954 9 12 9C13.1046 9 14 9.89543 14 11C14 12.1046 13.1046 13 12 13Z"></path>
            </svg>`
  }

  // Fit map to feature bounds
  fitToFeatures() {
    const fc = this._normalizedFC
    if (!fc || !fc.features || fc.features.length === 0) return

    if (!this._featureLayer) return

    try {
      const bounds = this._featureLayer.getBounds()

      if (bounds.isValid()) {
        this._map.fitBounds(bounds, {
          padding: [this.paddingValue, this.paddingValue],
          maxZoom: this.maxZoomValue,
          animate: false
        })

        // If single point, zoom out for context
        if (this.isSinglePoint(fc)) {
          this._map.zoomOut(4, { animate: false })
        }
      }
    } catch (e) {
      console.error("[Leaflet Basic] Error fitting to bounds:", e)
    }
  }

  // Check if feature collection contains only a single point
  isSinglePoint(fc) {
    return fc.type === "FeatureCollection" &&
           fc.features.length === 1 &&
           fc.features[0].geometry.type === "Point"
  }

  setupResizeObserver() {
    const container = this.hasMapTarget ? this.mapTarget : this.element
    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0 && this._map) {
          this._map.invalidateSize()
          this.fitToFeatures()
        }
      }
    })
    this._resizeObserver.observe(container)
  }

  cleanup() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }

    if (this._featureLayer) {
      this._map?.removeLayer(this._featureLayer)
      this._featureLayer = null
    }

    if (this._tileLayer) {
      this._map?.removeLayer(this._tileLayer)
      this._tileLayer = null
    }

    if (this._map) {
      this._map.remove()
      this._map = null
    }

    this._normalizedFC = null
  }
}
